import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from '../components/LegalLayout';
import { COMPANY } from '../lib/company';
import { useSEO } from '../hooks/useSEO';

const { emails } = COMPANY;

const sections = [
  {
    id: 'about',
    title: 'About us and these terms',
    body: (
      <>
        <p>
          These Terms of Service (&ldquo;Terms&rdquo;) form a contract between you and <strong>{COMPANY.legalName}</strong>, trading as {COMPANY.tradingName}, a company registered in {COMPANY.jurisdiction} under company number{' '}
          {COMPANY.companyNumber}, with its registered office at {COMPANY.address} (&ldquo;IP9&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). You can contact us at <a href={`mailto:${emails.support}`}>{emails.support}</a>.
        </p>
        <p>
          They apply to your use of ip9.io, the dashboard at dashboard.ip9.io, our API and our residential and mobile proxy services (the &ldquo;Services&rdquo;). Our{' '}
          <Link to="/acceptable-use-policy">Acceptable Use Policy</Link> and <Link to="/refund-policy">Refund &amp; Cancellation Policy</Link> form part of these Terms. Our{' '}
          <Link to="/privacy-policy">Privacy Policy</Link> explains how we handle personal data.
        </p>
        <p>
          Please read these Terms before you order. You can save or print them from this page at any time. The contract is concluded in English.
        </p>
      </>
    ),
  },
  {
    id: 'definitions',
    title: 'Who these terms apply to',
    body: (
      <>
        <p>
          A <strong>&ldquo;Consumer&rdquo;</strong> is an individual acting wholly or mainly outside their trade, business, craft or profession. A <strong>&ldquo;Business Customer&rdquo;</strong> is anyone else, including
          sole traders and organisations. Some clauses apply only to Consumers or only to Business Customers, and say so.
        </p>
        <p>
          If you are a Consumer, you have legal rights that these Terms do not affect, including under the UK Consumer Rights Act 2015 and the Consumer Contracts (Information, Cancellation and Additional Charges)
          Regulations 2013 or, if you live in the EU, the consumer protection law of your country of residence.
        </p>
      </>
    ),
  },
  {
    id: 'account',
    title: 'Eligibility and your account',
    body: (
      <ul>
        <li>You must be at least 18 years old and able to enter into a binding contract.</li>
        <li>If you accept these Terms on behalf of an organisation, you confirm you are authorised to bind it, and &ldquo;you&rdquo; means that organisation.</li>
        <li>You must give accurate registration and billing information and keep it up to date.</li>
        <li>You are responsible for keeping your login and proxy credentials confidential and for all activity under your account, including by any sub-users you create. Tell us immediately at {emails.support} if you suspect unauthorised use.</li>
        <li>To prevent fraud and abuse, we may ask you to verify your identity, your organisation or your intended use before activating or continuing the Services. We may decline to provide the Services if verification is not completed.</li>
      </ul>
    ),
  },
  {
    id: 'services',
    title: 'The Services',
    body: (
      <>
        <p>
          The Services let you route internet traffic through our network of residential and mobile IP addresses for lawful purposes such as market research, price monitoring, ad verification, SEO monitoring, quality assurance
          and geo-testing. Plan features, bandwidth allowances, locations and validity periods are described on our <Link to="/pricing">pricing page</Link> and in the dashboard at the time you order.
        </p>
        <p>
          We aim for high availability, but the Services depend on third-party networks and devices, so we do not guarantee that they will be uninterrupted or error-free, or that a specific IP address, location or carrier
          will always be available, unless we agree a written service level with you. We may carry out maintenance, and we will give advance notice of planned work where we reasonably can.
        </p>
        <p>
          We may improve or change the Services. If a change materially reduces what you have paid for, we will tell you in advance and, if you do not accept it, you may end your plan and receive a pro-rata refund of
          prepaid amounts for the affected period.
        </p>
      </>
    ),
  },
  {
    id: 'orders',
    title: 'Ordering and how the contract is formed',
    body: (
      <>
        <p>
          You order by choosing a plan in the dashboard, reviewing the order summary (where you can correct any input errors before you submit), and confirming payment. Your order is an offer to buy. The contract is formed
          when we confirm the order by email or in the dashboard and make the Services available. We keep a record of your order, which you can see in the dashboard.
        </p>
        <p>We may refuse an order, for example if we cannot verify your details, suspect fraud or misuse, or if a price was clearly displayed in error. If we refuse after taking payment, we will refund you in full.</p>
      </>
    ),
  },
  {
    id: 'prices',
    title: 'Prices and payment',
    body: (
      <>
        <ul>
          <li>Prices are shown on our website and in the dashboard. For Consumers, the price shown at checkout includes any applicable VAT. For Business Customers, prices exclude VAT and other taxes, which are added where applicable.</li>
          <li>Payment is due in advance, using the methods offered at checkout. Bandwidth-based plans are valid for the period stated when you buy them; unused bandwidth expires at the end of that period unless the plan says otherwise.</li>
          <li>If a plan renews automatically, we will make that clear before you buy, remind you as required by law, and you can switch off renewal in the dashboard at any time before the next billing date.</li>
          <li>We may change prices for future periods. Changes do not affect plans you have already paid for. We will give at least 30 days&rsquo; notice of any price change to a renewing plan, and you can cancel before it takes effect.</li>
          <li>If a payment fails or is reversed, we may suspend the Services until the amount is paid. Business Customers must pay undisputed invoices within the period stated on the invoice.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'withdrawal',
    title: 'Cancellation and the right of withdrawal',
    body: (
      <>
        <p>
          <strong>Consumers</strong> in the UK and EU have a statutory right to withdraw from the contract within 14 days of the day it is formed, without giving a reason. Because proxy access starts immediately, at checkout
          you will be asked to expressly request that the Services start during the withdrawal period. If you then withdraw, you must pay for the Services provided up to the time you tell us, in proportion to the
          whole contract (for bandwidth plans, the share of bandwidth used). If the Services have been fully provided within the 14 days at your request, the right of withdrawal ends.
        </p>
        <p>
          How to withdraw, our model withdrawal form, and our additional refund terms for all customers are set out in the <Link to="/refund-policy">Refund &amp; Cancellation Policy</Link>.
        </p>
        <p>You may stop using the Services and close your account at any time from the dashboard or by contacting us.</p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable use and your responsibilities',
    body: (
      <>
        <p>
          You must use the Services lawfully and in line with our <Link to="/acceptable-use-policy">Acceptable Use Policy</Link>. In particular, you are responsible for:
        </p>
        <ul>
          <li>complying with all laws that apply to you and to the targets you access, including data protection, computer misuse, consumer protection, intellectual property, sanctions and export control laws;</li>
          <li>having a lawful basis for any personal data you collect or process using the Services. You act as an independent controller of that data, and we do not inspect its content;</li>
          <li>respecting reasonable rate limits and not degrading the availability of websites you access; and</li>
          <li>the actions of anyone who uses the Services through your account or credentials.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'suspension',
    title: 'Suspension and termination',
    body: (
      <>
        <p>We may suspend or terminate your access to the Services, in whole or in part, if:</p>
        <ul>
          <li>you materially or repeatedly breach these Terms or the Acceptable Use Policy;</li>
          <li>we reasonably suspect fraud, abuse or unlawful use, or receive credible abuse complaints about your traffic;</li>
          <li>a payment is overdue or reversed; or</li>
          <li>we are required to by law, a court or a competent authority.</li>
        </ul>
        <p>
          Where reasonable and lawful, we will tell you the reason and give you an opportunity to respond. You can contest a suspension by writing to <a href={`mailto:${emails.support}`}>{emails.support}</a>. If we end the
          contract for reasons that are not your fault, we will refund prepaid amounts for the period after termination. If we end it because of your serious breach, we may keep amounts already paid to the extent the law
          allows.
        </p>
        <p>Clauses that by their nature should survive termination (including payment, liability, indemnity and governing law) continue to apply.</p>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual property',
    body: (
      <p>
        We and our licensors own all rights in the Services, the dashboard, the API, our documentation and this website. We grant you a limited, non-exclusive, non-transferable licence to use them during your contract for the
        purposes allowed by these Terms. You must not copy, resell, reverse engineer or build a competing service from them except where the law allows. If you send us feedback, we may use it without obligation to you.
      </p>
    ),
  },
  {
    id: 'consumer-rights',
    title: 'Our commitments and your statutory rights',
    body: (
      <>
        <p>
          We will provide the Services with reasonable care and skill and as described. If you are a Consumer and the Services are not provided as described or with reasonable care and skill, you are entitled to ask us to
          put it right or, if we cannot, to a price reduction or refund. Digital content and services must also conform to the contract under the law of your country. Nothing in these Terms limits these rights.
        </p>
        <p>
          For Business Customers, except as expressly set out in these Terms, the Services are provided &ldquo;as is&rdquo;, and all other warranties, conditions and terms implied by law are excluded to the fullest extent permitted.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Liability',
    body: (
      <>
        <p>
          <strong>Nothing in these Terms excludes or limits our liability</strong> for death or personal injury caused by our negligence, for fraud or fraudulent misrepresentation, or for anything else that cannot be excluded
          or limited by law.
        </p>
        <p>
          <strong>If you are a Consumer:</strong> we are responsible for loss or damage you suffer that is a foreseeable result of our breach of these Terms or our failure to use reasonable care and skill. We are not
          responsible for loss that was not foreseeable, or for business losses, because the Services are supplied for private use.
        </p>
        <p>
          <strong>If you are a Business Customer:</strong> we are not liable for any loss of profits, revenue, business, goodwill, anticipated savings or data, or for any indirect or consequential loss. Our total liability
          arising out of or in connection with the contract, whether in contract, tort (including negligence) or otherwise, is limited to the fees you paid us in the 12 months before the event giving rise to the claim.
        </p>
        <p>We are not responsible for the content, availability or terms of websites and services you access using the Services.</p>
      </>
    ),
  },
  {
    id: 'indemnity',
    title: 'Indemnity (Business Customers)',
    body: (
      <p>
        If you are a Business Customer, you will indemnify us against claims, losses, fines and reasonable costs (including legal fees) arising from your breach of these Terms or the Acceptable Use Policy, or from unlawful use
        of the Services through your account.
      </p>
    ),
  },
  {
    id: 'compliance',
    title: 'Sanctions and export control',
    body: (
      <p>
        You confirm that you are not located in, or owned or controlled by a person in, a country or territory subject to comprehensive UK, EU, UN or US sanctions, and that you are not listed on any sanctions list. You must
        not use the Services to deal with sanctioned persons or to circumvent sanctions or export controls. We may suspend the Services immediately to comply with sanctions law.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    body: (
      <p>
        We may update these Terms to reflect changes in law, our Services or our business. We will give you at least 30 days&rsquo; notice of material changes by email or in the dashboard. Changes do not apply to plans already
        paid for until they renew. If you do not agree to a change, you can cancel before it takes effect and receive a pro-rata refund of prepaid amounts for the remaining period.
      </p>
    ),
  },
  {
    id: 'force-majeure',
    title: 'Events outside our control',
    body: (
      <p>
        We are not responsible for delay or failure caused by events outside our reasonable control, such as failures of upstream carriers or networks, power or internet outages, cyberattacks, natural disasters or government
        action. We will tell you and take reasonable steps to minimise the effect. If the event lasts more than 30 days, either of us may end the affected plan and you will receive a refund for the unused part.
      </p>
    ),
  },
  {
    id: 'general',
    title: 'General',
    body: (
      <ul>
        <li>We may transfer our rights and obligations to another organisation. We will tell you, and this will not reduce your rights. You may transfer yours only with our written consent.</li>
        <li>These Terms and the documents they refer to are the entire agreement between us about the Services. For Business Customers, they replace any earlier discussions and any terms in your purchase documents.</li>
        <li>If a court finds part of these Terms unenforceable, the rest stays in effect.</li>
        <li>If we do not enforce a right immediately, we can still enforce it later.</li>
        <li>No one other than you and us has any right to enforce these Terms under the Contracts (Rights of Third Parties) Act 1999.</li>
      </ul>
    ),
  },
  {
    id: 'law',
    title: 'Governing law and disputes',
    body: (
      <>
        <p>
          These Terms are governed by the law of England and Wales. For Business Customers, the courts of England and Wales have exclusive jurisdiction.
        </p>
        <p>
          If you are a Consumer, you may bring proceedings in the courts of England and Wales or, if you live in Scotland, Northern Ireland or an EU member state, in the courts of the place where you live. You also keep the
          protection of the mandatory consumer laws of the country where you live.
        </p>
        <p>
          <strong>Complaints.</strong> Please contact <a href={`mailto:${emails.support}`}>{emails.support}</a> first. We aim to acknowledge complaints within 2 business days and to resolve them within 14 days. If we cannot resolve
          a complaint from a Consumer, we will tell you of a relevant alternative dispute resolution provider and whether we agree to use it.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact and notices',
    body: (
      <p>
        Support: <a href={`mailto:${emails.support}`}>{emails.support}</a> · Legal notices: <a href={`mailto:${emails.legal}`}>{emails.legal}</a> or by post to {COMPANY.legalName}, {COMPANY.address} · Abuse reports:{' '}
        <a href={`mailto:${emails.abuse}`}>{emails.abuse}</a>. We may send notices to the email address on your account.
      </p>
    ),
  },
];

const TermsOfServicePage = () => {
  useSEO('termsOfService');
  return <LegalLayout title="Terms of Service" sections={sections} />;
};

export default TermsOfServicePage;
